/**
 * Shared Sector / Sub-sector parser (BE Issue #5, 2026-07-07).
 *
 * FE mirror of `backend/src/services/analysis/dd/subsector_parser.py`.
 * Both must produce byte-identical output for the same input; a
 * round-trip test guards against drift.
 *
 * See the Python module docstring for the full separator taxonomy
 * and semantic rules.
 */

interface SectorSubsector {
  sector: string | null;
  subsector: string | null;
}

// Ordered — parenthetical wins over other separators.
const _PATTERNS: readonly RegExp[] = [
  // Parenthetical  "Parent (Child)"
  /^\s*(?<parent>[^()]+?)\s*\(\s*(?<child>[^()]+?)\s*\)\s*$/,
  // Em-dash  U+2014
  /^\s*(?<parent>[^—]+?)\s*—\s*(?<child>[^—]+?)\s*$/,
  // En-dash  U+2013
  /^\s*(?<parent>[^–]+?)\s*–\s*(?<child>[^–]+?)\s*$/,
  // Plain ASCII hyphen "-" — REQUIRES whitespace on both sides so
  // compound words ("Lithium-Ion" / "Non-Renewable") are not falsely
  // split. Uses `.+?` (not `[^-]+?`) so parent / child may
  // themselves contain hyphenated compounds — the `\s+-\s+`
  // boundary is the only match anchor.
  // Sprint 5 client feedback 2026-07-08 — EH2 Investment Memo p.2
  // phrase "Sector: Green Hydrogen - Electrolyser Industry" splits
  // via this rule.
  /^\s*(?<parent>.+?)\s+-\s+(?<child>.+?)\s*$/,
  // Colon
  /^\s*(?<parent>[^:]+?)\s*:\s*(?<child>[^:]+?)\s*$/,
  // Slash
  /^\s*(?<parent>[^/]+?)\s*\/\s*(?<child>[^/]+?)\s*$/,
  // Comma
  /^\s*(?<parent>[^,]+?)\s*,\s*(?<child>[^,]+?)\s*$/,
];

/**
 * Split `raw` into `{ sector, subsector }` using the first separator
 * pattern that matches.
 *
 *   * null / empty / whitespace-only → both null
 *   * single value (no separator matches) → sector = raw, subsector = null
 *   * whitespace around each part is stripped
 *
 * Examples:
 *   parseSectorSubsector("Energy Transition (Lithium-Ion Battery)")
 *     → { sector: "Energy Transition", subsector: "Lithium-Ion Battery" }
 *   parseSectorSubsector("Energy — Battery Storage")
 *     → { sector: "Energy", subsector: "Battery Storage" }
 *   parseSectorSubsector("Renewables")
 *     → { sector: "Renewables", subsector: null }
 *   parseSectorSubsector(null)
 *     → { sector: null, subsector: null }
 */
export function parseSectorSubsector(
  raw: string | null | undefined,
): SectorSubsector {
  if (raw == null) return { sector: null, subsector: null };
  const s = String(raw).trim();
  if (!s) return { sector: null, subsector: null };

  for (const pattern of _PATTERNS) {
    const m = pattern.exec(s);
    if (m && m.groups) {
      const parent = (m.groups.parent ?? '').trim();
      const child = (m.groups.child ?? '').trim();
      if (parent && child) {
        return { sector: parent, subsector: child };
      }
    }
  }

  // No separator matched — treat the whole phrase as the sector.
  return { sector: s, subsector: null };
}
